// src/pages/customer/customerpanel/BrowsePanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useCart } from '../useCart';
import BrowseMeatPanel from './BrowseMeatPanel';
import api from '../../../utils/api';

const CATEGORIES = ['All', 'Goat', 'Sheep', 'Bakra', 'Other'];

export default function BrowsePanel({ user, onOpenCart, onBuyNow }) {
  const [animals,   setAnimals]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [search,    setSearch]    = useState('');
  const [category,  setCategory]  = useState('All');
  const [minPrice,  setMinPrice]  = useState('');
  const [maxPrice,  setMaxPrice]  = useState('');
  const [selected,  setSelected]  = useState(null); 
  const { addToCart, isInCart, flushSync } = useCart(user);
  const [inquiryAnimal, setInquiryAnimal] = useState(null);
  const [inquiryMsg,    setInquiryMsg]    = useState('');
  const [inquirySent,   setInquirySent]   = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [qty, setQty] = useState(1);
  const [quickBuy, setQuickBuy] = useState(null);
  const [browseMode, setBrowseMode] = useState('animals');

  const fetchAnimals = useCallback(async () => {
  setLoading(true); setError('');
  try {
    const params = {};
    if (category !== 'All') params.category = category;
    if (search)    params.search    = search;
    if (minPrice)  params.min_price = minPrice;
    if (maxPrice)  params.max_price = maxPrice;

    const { data } = await api.get('/api/public/browse', { params });
    setAnimals(data);
  } catch {
    setError('Could not load listings. Please try again.');
  } finally { setLoading(false); }
}, [category, search, minPrice, maxPrice]);

  useEffect(() => { fetchAnimals(); }, [category, fetchAnimals]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAnimals();
  };

  const handleSendInquiry = async () => {
    if (!inquiryMsg.trim()) return;
    setInquiryLoading(true);
    try {
      await api.post('/api/customer/inquiries', {
        animal_id: inquiryAnimal.id,
        seller_id: inquiryAnimal.seller_id, 
        message:   inquiryMsg,
      }
    );
      setInquirySent(true);
      setInquiryMsg('');
    } catch {
      alert('Could not send inquiry. Please login first.');
    } finally { setInquiryLoading(false); }
  };

  return (
  <>
  <div className="space-y-6">
    {/* Toggle */}
    <div className="flex gap-2 bg-white rounded-2xl p-1.5 shadow-sm border border-outline-variant/10 w-fit">
      <button onClick={() => setBrowseMode('animals')}
        className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${
          browseMode === 'animals' ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'
        }`}>
        🐐 Live Animals
      </button>
      <button onClick={() => setBrowseMode('meat')}
        className={`px-5 py-2 rounded-xl text-sm font-bold transition-all ${
          browseMode === 'meat' ? 'bg-primary text-white' : 'text-secondary hover:bg-surface-container'
        }`}>
        🥩 Meat Products
      </button>
    </div>

    {browseMode === 'meat' ? <BrowseMeatPanel user={user} onBuyNow={onBuyNow} /> : (<>
    {/* Header */}
    <div>
      <h2 className="text-xl font-black text-on-surface">Browse Livestock</h2>
        <p className="text-sm text-secondary mt-1">
          Explore verified animals from trusted farmers.
        </p>
      </div>

      {/* Search + Filters */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-outline-variant/10">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-end">
          {/* Search box */}
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] font-black uppercase tracking-widest text-secondary block mb-1">
              Search
            </label>
            <div className="relative">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2
                               text-outline text-[18px]">search</span>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Name, breed, description..."
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-outline-variant/40
                           text-sm outline-none focus:border-primary focus:ring-2
                           focus:ring-primary/10 transition-all" />
            </div>
          </div>

          {/* Min price */}
          <div className="w-32">
            <label className="text-[10px] font-black uppercase tracking-widest text-secondary block mb-1">
              Min ₹
            </label>
            <input type="number" value={minPrice} onChange={e => setMinPrice(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2.5 rounded-xl border border-outline-variant/40
                         text-sm outline-none focus:border-primary transition-all" />
          </div>

          {/* Max price */}
          <div className="w-32">
            <label className="text-[10px] font-black uppercase tracking-widest text-secondary block mb-1">
              Max ₹
            </label>
            <input type="number" value={maxPrice} onChange={e => setMaxPrice(e.target.value)}
              placeholder="Any"
              className="w-full px-3 py-2.5 rounded-xl border border-outline-variant/40
                         text-sm outline-none focus:border-primary transition-all" />
          </div>

          <button type="submit"
            className="px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-bold
                       hover:opacity-90 transition-all">
            Search
          </button>
          <button type="button" onClick={() => {
              setSearch(''); setMinPrice(''); setMaxPrice('');
              setCategory('All'); setTimeout(fetchAnimals, 50);
            }}
            className="px-4 py-2.5 rounded-xl border border-outline-variant text-secondary
                       text-sm font-semibold hover:bg-surface-container transition-all">
            Reset
          </button>
        </form>

        {/* Category tabs */}
        <div className="flex gap-2 mt-3 flex-wrap">
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCategory(c)}
              className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                category === c
                  ? 'bg-primary text-white'
                  : 'bg-surface-container text-secondary hover:bg-surface-container-high'
              }`}>
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3
                        rounded-xl text-sm flex items-center gap-2">
          <span className="material-symbols-outlined text-base">error</span>
          {error}
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm
                                    border border-outline-variant/10 animate-pulse">
              <div className="h-48 bg-surface-container" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-surface-container rounded w-3/4" />
                <div className="h-3 bg-surface-container rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : animals.length === 0 ? (
        <div className="bg-white rounded-2xl p-14 shadow-sm border border-outline-variant/10
                        flex flex-col items-center text-center gap-3">
          <span className="material-symbols-outlined text-[48px] text-outline">
            sentiment_dissatisfied
          </span>
          <p className="text-lg font-black text-on-surface">No animals found</p>
          <p className="text-sm text-secondary">Try adjusting your filters or search term.</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-secondary font-semibold">
            {animals.length} listing{animals.length !== 1 ? 's' : ''} found
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {animals.map(a => (
              <div key={a.id}
                className={`bg-white rounded-2xl overflow-hidden shadow-sm border transition-all cursor-pointer group ${
                  a.status === 'sold'
                    ? 'border-outline-variant/10 hover:shadow-md hover:border-orange-200'
                    : 'border-outline-variant/10 hover:shadow-md hover:border-primary/20'
                }`}
                onClick={() => setSelected(a)}>
                {/* Image */}
                <div className="h-36 sm:h-48 bg-surface-container overflow-hidden relative">
                  {a.image_url ? (
                    <img src={a.image_url.startsWith('/uploads')
                                ? `http://localhost:4000${a.image_url}`
                                : a.image_url}
                         alt={a.name}
                         className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <span className="material-symbols-outlined text-[48px] text-outline">pets</span>
                    </div>
                  )}
                  <span className="absolute top-2 left-2 bg-white/90 backdrop-blur-sm
                                   text-[10px] font-black px-2 py-1 rounded-full text-primary">
                    {a.category}
                  </span>
                  {a.status === 'sold' ? (
                    <span className="absolute top-2 right-2 bg-orange-500 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                      Sold Out
                    </span>
                    ) : (
                      <span className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold px-2 py-1 rounded-full">
                        {a.quantity} left
                      </span>
                  )}
                </div>

                {/* Info */}
                <div className="p-3">
                  <p className="font-black text-sm text-on-surface leading-tight truncate">{a.name}</p>
                  <p className="text-xs text-secondary truncate mt-0.5">{a.breed || 'Mixed breed'}</p>

                  <p className="text-primary font-black text-base mt-1.5">
                    ₹{Number(a.price_inr).toLocaleString('en-IN')}
                  </p>

                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-outline-variant/10 gap-1">
                    <span className="flex items-center gap-0.5 text-[10px] text-secondary min-w-0">
                      <span className="material-symbols-outlined text-[11px] flex-shrink-0 opacity-60">scale</span>
                      <span className="truncate">{a.weight_kg ? `${a.weight_kg}kg` : '—'}</span>
                    </span>
                    <span className="flex items-center gap-0.5 text-[10px] text-secondary min-w-0 justify-end">
                      <span className="material-symbols-outlined text-[11px] flex-shrink-0 opacity-60">location_on</span>
                      <span className="truncate max-w-[60px]">{a.location_city || '—'}</span>
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-1.5 sm:gap-2 mt-3">
                    {a.status === 'sold' ? (
                      <button disabled className="w-full sm:flex-1 py-2 rounded-xl text-xs font-bold
                        bg-orange-50 text-orange-400 border border-orange-100 cursor-not-allowed">
                        Sold Out
                      </button>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); setQuickBuy(a); }}
                        className="w-full sm:flex-1 py-2 rounded-xl text-xs font-bold bg-primary text-white
                                  hover:opacity-90 transition-all flex items-center justify-center gap-1">
                        <span className="material-symbols-outlined text-[14px]">shopping_cart</span>
                        Buy / Add
                      </button>
                    )}
                    <button
                      onClick={e => { e.stopPropagation(); setSelected(a); }}
                      className="w-full sm:flex-1 py-2 rounded-xl border border-outline-variant text-secondary
                                text-xs font-bold hover:bg-surface-container transition-all">
                      Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
       </>)} 
    </div>

    {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center px-3"
             onClick={() => setSelected(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl flex flex-col" style={{maxHeight:'90vh'}}
               onClick={e => e.stopPropagation()}>
            {/* Image */}
            <div className="h-32 sm:h-48 bg-surface-container relative rounded-t-2xl overflow-hidden flex-shrink-0">
              {selected.image_url ? (
                <img src={selected.image_url.startsWith('/uploads')
                            ? `http://localhost:4000${selected.image_url}`
                            : selected.image_url}
                     alt={selected.name}
                     className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-[64px] text-outline">pets</span>
                </div>
              )}
              <button onClick={() => setSelected(null)}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40
                           text-white flex items-center justify-center hover:bg-black/60">
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>

            {/* Details */}
            <div className="p-3 space-y-2 overflow-y-auto flex-1">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-sm font-black text-on-surface">{selected.name}</h3>
                  <p className="text-xs text-secondary">{selected.breed} · {selected.category}</p>
                </div>
                <p className="text-base font-black text-primary">
                  ₹{Number(selected.price_inr).toLocaleString('en-IN')}
                  <span className="text-xs font-semibold text-secondary ml-1">per animal</span>
                </p>
                {qty > 1 && (
                  <div className="mt-1 flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <span className="text-[11px] text-emerald-700 font-semibold">
                      ₹{Number(selected.price_inr).toLocaleString('en-IN')} × {qty} =
                    </span>
                    <span className="text-[13px] text-emerald-800 font-black">
                      ₹{(Number(selected.price_inr) * qty).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </div>

              {selected.status === 'sold' && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
                  <p className="text-xs font-bold text-orange-800 flex items-center gap-2">
                    <span className="material-symbols-outlined text-[16px]">info</span>
                    This animal has been sold. You can still send an inquiry to explore similar animals.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { icon: 'scale',       label: 'Weight',  val: selected.weight_kg   ? `${selected.weight_kg}kg`      : '—' },
                  { icon: 'cake',        label: 'Age',     val: selected.age_months   ? `${selected.age_months} mo`    : '—' },
                  { icon: 'dentistry',   label: 'Teeth',   val: selected.teeth_count  ? `${selected.teeth_count}`      : '—' },
                  { icon: 'location_on', label: 'City',    val: selected.location_city  || '—' },
                  { icon: 'map',         label: 'State',   val: selected.location_state || '—' },
                  { icon: 'vaccines',    label: 'Health',  val: selected.health_status  || '—' },
                ].map(({ icon, label, val }) => (
                  <div key={label} className="bg-surface-container-low rounded-lg p-1.5 text-center">
                    <span className="material-symbols-outlined text-primary text-[14px]">{icon}</span>
                    <p className="text-[8px] text-secondary font-bold uppercase">{label}</p>
                    <p className="text-[10px] font-black text-on-surface truncate">{val}</p>
                  </div>
                ))}
              </div>

              {selected.description && (
                <div className="bg-surface-container-low rounded-lg p-2">
                  <p className="text-[9px] font-black uppercase text-secondary mb-0.5">Description</p>
                  <p className="text-xs text-on-surface">{selected.description}</p>
                </div>
              )}

              {selected.seller_name && (
                <div className="flex items-center gap-2 bg-emerald-50 rounded-lg p-2">
                  <div className="w-6 h-6 rounded-full bg-emerald-600 flex items-center
                                  justify-center text-white font-black text-xs">
                    {selected.seller_name[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="text-xs font-black text-on-surface">{selected.seller_name}</p>
                    <p className="text-[10px] text-secondary">Verified Farmer</p>
                  </div>
                </div>
              )}

              <div className="flex gap-3 items-end">
                {selected.status === 'sold' ? (
                  <div className="flex-1">
                    <button disabled
                      className="w-full py-2 text-xs rounded-xl font-bold transition-all flex items-center justify-center gap-2 bg-orange-50 text-orange-400 border border-orange-100 cursor-not-allowed">
                      <span className="material-symbols-outlined text-[18px]">shopping_cart_off</span>
                      Sold Out - Cannot Add to Cart
                    </button>
                  </div>
                ) : (
                  <div className="flex-1 flex flex-col gap-2">
                    {!isInCart(selected.id) && (
                      <>
                        <div className="flex items-center justify-center gap-2">
                          <button onClick={() => setQty(q => Math.max(1, q - 1))}
                            className="w-8 h-8 rounded-full border border-outline-variant/30
                                      flex items-center justify-center font-bold text-secondary
                                      hover:bg-surface-container transition-all">−</button>
                          <span className="w-8 text-center font-bold text-sm">{qty}</span>
                          <button onClick={() => setQty(q => Math.min(selected.quantity || 99, q + 1))}
                            className="w-8 h-8 rounded-full border border-outline-variant/30
                                      flex items-center justify-center font-bold text-secondary
                                      hover:bg-surface-container transition-all">+</button>
                        </div>
                        {qty >= selected.quantity && (
                          <p className="text-center text-[10px] text-orange-600 font-semibold mt-1">
                            Only {selected.quantity} available
                          </p>
                        )}
                     </>
                  )}
                  <button
                    onClick={() => { addToCart(selected, qty); setQty(1); }}
                    className={`w-full py-2 text-xs rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                      isInCart(selected.id)
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'border-2 border-primary text-primary hover:bg-primary/5'
                    }`}>
                    <span className="material-symbols-outlined text-[18px]">shopping_cart</span>
                    {isInCart(selected.id) ? 'Added to Cart' : 'Add to Cart'}
                  </button>
                </div>
                  )}
                <button
                  onClick={() => { setInquiryAnimal(selected); setInquirySent(false); }}
                  className="flex-1 py-2 text-xs rounded-xl bg-primary text-white font-bold
                            hover:opacity-90 transition-all flex items-center justify-center gap-1">
                  <span className="material-symbols-outlined text-[14px]">forum</span>
                  {selected.status === 'sold' ? 'Ask About Similar' : 'Send Inquiry'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Inquiry Modal */}
      {inquiryAnimal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => { setInquiryAnimal(null); setInquirySent(false); }}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4"
              onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-black text-lg">Send Inquiry</h3>
                <p className="text-sm text-secondary">{inquiryAnimal.name}</p>
                {inquiryAnimal.status === 'sold' && (
                  <span className="text-xs text-orange-600 font-semibold">This animal is sold out</span>
                )}
              </div>
              <button onClick={() => { setInquiryAnimal(null); setInquirySent(false); }}
                className="w-8 h-8 rounded-full bg-surface-container flex items-center justify-center">
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>

            {inquirySent ? (
              <div className="text-center py-6 space-y-2">
                <span className="material-symbols-outlined text-[48px] text-emerald-500">check_circle</span>
                <p className="font-black text-on-surface">Inquiry Sent!</p>
                <p className="text-sm text-secondary">
                  {inquiryAnimal.status === 'sold' 
                    ? 'The farmer will contact you about similar animals available.'
                    : 'The farmer will reply soon.'}
                </p>
                <button onClick={() => { setInquiryAnimal(null); setInquirySent(false); }}
                  className="mt-2 px-6 py-2 rounded-xl bg-primary text-white font-bold text-sm">
                  Done
                </button>
              </div>
            ) : (
              <>
                <textarea
                  value={inquiryMsg}
                  onChange={e => setInquiryMsg(e.target.value)}
                  placeholder={inquiryAnimal.status === 'sold' 
                    ? "Ask about similar animals available, pricing, delivery..."
                    : "Ask about age, health, delivery, price negotiation..."}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border border-outline-variant/40 text-sm
                            outline-none focus:border-primary focus:ring-2 focus:ring-primary/10
                            resize-none transition-all" />
                <button
                  onClick={handleSendInquiry}
                  disabled={inquiryLoading || !inquiryMsg.trim()}
                  className="w-full py-3 rounded-xl bg-primary text-white font-bold
                            hover:opacity-90 disabled:opacity-50 transition-all">
                  {inquiryLoading ? 'Sending...' : 'Send Message'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {/* Quick-Buy Popup */}
      {quickBuy && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center
                        justify-center sm:px-4"
            onClick={() => { setQuickBuy(null); setQty(1); }}>
          <div className="bg-white w-full sm:max-w-sm rounded-t-2xl sm:rounded-2xl
                          shadow-2xl overflow-hidden"
              onClick={e => e.stopPropagation()}>

            {/* Header row */}
            <div className="flex items-center gap-3 p-4 border-b border-outline-variant/10">
              <div className="w-14 h-14 rounded-xl overflow-hidden bg-surface-container flex-shrink-0
                              flex items-center justify-center">
                {quickBuy.image_url ? (
                  <img src={quickBuy.image_url.startsWith('/uploads')
                              ? `http://localhost:4000${quickBuy.image_url}`
                              : quickBuy.image_url}
                      alt={quickBuy.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="material-symbols-outlined text-[28px] text-outline">pets</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-black text-sm text-on-surface truncate">{quickBuy.name}</p>
                <p className="text-xs text-secondary">{quickBuy.breed || 'Mixed breed'} · {quickBuy.category}</p>
                <p className="text-base font-black text-primary mt-0.5">
                  ₹{Number(quickBuy.price_inr).toLocaleString('en-IN')}
                  <span className="text-xs font-semibold text-secondary ml-1">per animal</span>
                </p>
                {qty > 1 && (
                  <div className="mt-1 flex items-center gap-1 px-2 py-1 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <span className="text-[11px] text-emerald-700 font-semibold">
                      ₹{Number(quickBuy.price_inr).toLocaleString('en-IN')} × {qty} =
                    </span>
                    <span className="text-[13px] text-emerald-800 font-black">
                      ₹{(Number(quickBuy.price_inr) * qty).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </div>
              <button onClick={() => { setQuickBuy(null); setQty(1); }}
                className="w-7 h-7 rounded-full bg-surface-container flex items-center
                          justify-center text-secondary flex-shrink-0 self-start">
                <span className="material-symbols-outlined text-[16px]">close</span>
              </button>
            </div>

            {/* Quantity + actions */}
            <div className="p-4 space-y-3">
              {/* Qty selector */}
              <div className="flex items-center gap-3">
                <p className="text-xs font-black uppercase tracking-widest text-secondary w-8">Qty</p>
                <div className="flex items-center gap-2 bg-surface-container rounded-xl px-3 py-1.5">
                  <button onClick={() => setQty(q => Math.max(1, q - 1))}
                    disabled={qty <= 1}
                    className="w-7 h-7 flex items-center justify-center text-lg font-bold
                              text-secondary hover:text-on-surface disabled:opacity-30 transition-all">
                    −
                  </button>
                  <span className="w-8 text-center font-black text-sm">{qty}</span>
                  <button onClick={() => setQty(q => Math.min(quickBuy.quantity || 99, q + 1))}
                    disabled={qty >= (quickBuy.quantity || 99)}
                    className="w-7 h-7 flex items-center justify-center text-lg font-bold
                              text-secondary hover:text-on-surface disabled:opacity-30 transition-all">
                    +
                  </button>
                </div>
                {qty >= quickBuy.quantity && (
                  <p className="text-[10px] text-orange-600 font-semibold">
                    Only {quickBuy.quantity} available
                  </p>
                )}
                {quickBuy.location_city && (
                  <span className="text-[10px] text-secondary flex items-center gap-0.5">
                    <span className="material-symbols-outlined text-[12px]">location_on</span>
                    {quickBuy.location_city}
                  </span>
                )}
              </div>

              {/* Add to Cart */}
              {isInCart(quickBuy.id) ? (
                <div className="w-full py-2.5 rounded-xl text-sm font-bold text-center
                                bg-emerald-100 text-emerald-700 border border-emerald-200">
                  ✓ Already in Cart
                </div>
              ) : (
                <button
                  onClick={() => { addToCart(quickBuy, qty); setQty(1); }}
                  className="w-full py-2.5 rounded-xl border-2 border-primary text-primary
                            text-sm font-bold hover:bg-primary/5 transition-all
                            flex items-center justify-center gap-2">
                  <span className="material-symbols-outlined text-[18px]">shopping_cart</span>
                  Add to Cart
                </button>
              )}

              {/* Buy Now */}
              <button
                onClick={async () => {
                  if (!isInCart(quickBuy.id)) addToCart(quickBuy, qty);
                  setQuickBuy(null); setQty(1);
                  await flushSync();
                  onBuyNow?.();
                }}
                className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-bold
                          hover:opacity-90 transition-all flex items-center justify-center gap-2">
                <span className="material-symbols-outlined text-[18px]">bolt</span>
                Buy Now
              </button>

              <button
                onClick={() => { setSelected(quickBuy); setQuickBuy(null); }}
                className="w-full py-2 text-xs text-secondary font-semibold
                          hover:text-on-surface transition-all text-center">
                View full details →
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}